import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import DirectionsRunOutlinedIcon from "@mui/icons-material/DirectionsRunOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import { ActorState } from "../../models/ActorState";
import { ActorMovementType } from "../../models/ActorMovementType";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import PiSegmentedToggle, {
  PiSegmentedToggleOption,
} from "../../components/PiSegmentedToggle";
import "./ActorStateMovementToggle.css";

export default function ActorStateMovementToggle(props: {
  actor: ActorState;
  onChange: (movement: ActorMovementType) => void;
  disabled: boolean;
  disabledReason?: string;
  variant?: "default" | "compact";
  /** Leave out the "Movement" heading, e.g. when a settings row already labels it. */
  hideLabel?: boolean;
}) {
  const {
    actor,
    onChange,
    disabled,
    disabledReason,
    variant = "default",
    hideLabel = false,
  } = props;
  const { t } = useTranslation();

  const options: PiSegmentedToggleOption<ActorMovementType>[] = [
    {
      testId: "movement-instant",
      ariaLabel: String(t("instant")),
      label:
        variant === "compact" ? (
          <BoltOutlinedIcon fontSize="inherit" />
        ) : (
          <>
            <BoltOutlinedIcon fontSize="inherit" />
            <span>{t("instant")}</span>
          </>
        ),
      value: ActorMovementType.Instant,
    },
    {
      testId: "movement-walk",
      ariaLabel: String(t("walk")),
      label:
        variant === "compact" ? (
          <DirectionsRunOutlinedIcon fontSize="inherit" />
        ) : (
          <>
            <DirectionsRunOutlinedIcon fontSize="inherit" />
            <span>{t("walk")}</span>
          </>
        ),
      value: ActorMovementType.Walk,
    },
  ];

  if (actor.actorId === "player") {
    options.push({
      testId: "movement-auto-walk",
      ariaLabel: String(t("auto_walk")),
      title: String(t("auto_walk_tooltip")),
      label: (
        <>
          <PlayArrowOutlinedIcon fontSize="inherit" />
          {variant !== "compact" && <span>{t("auto_walk")}</span>}
        </>
      ),
      value: ActorMovementType.AutoWalk,
    });
    options.push({
      testId: "movement-explore",
      ariaLabel: String(t("explore")),
      title: String(t("explore_tooltip")),
      label: (
        <>
          <ExploreOutlinedIcon fontSize="inherit" />
          {variant !== "compact" && <span>{t("explore")}</span>}
        </>
      ),
      value: ActorMovementType.Explore,
    });
  }

  return (
    <div className={"flex flex-col gap-[8px]"}>
      {!hideLabel && (
        <div className={"text-creator-text text-[14px] font-medium"}>
          {t("movement")}
        </div>
      )}
      <Tooltip
        title={disabledReason ?? ""}
        arrow
        describeChild
        placement={variant === "compact" ? "right" : "top"}
      >
        <div
          data-testid="movement-toggle-tooltip-target"
          role={disabledReason ? "group" : undefined}
          aria-label={disabledReason ? "Movement unavailable" : undefined}
          tabIndex={disabledReason ? 0 : undefined}
        >
          <PiSegmentedToggle
            ariaLabel="movement-toggle"
            className={actor.actorId === "player" && variant === "default" ? "actor-movement-player-labelled" : undefined}
            disabled={disabled}
            fullWidth
            layoutId={`actorstate-movement-toggle-${variant}`}
            options={options}
            value={actor.movement}
            onChange={onChange}
          />
        </div>
      </Tooltip>
    </div>
  );
}
